/* ═══════════════════════════════════════════════════════════════
   The Designer — the unified project editor's workspace.

   ONE surface, no modes. The body is always a text editor; the header,
   footer, pre-body and post-body bands are always zone-interactive —
   worked ON THE PAPER exactly as in Forma Studio (ZoneBandEdit: click,
   Ctrl/Shift-click, marquee, drag onto a slot or a band, right-click).

   The forma is edited by Forma Studio's REAL parts (Josh's ruling,
   docs/PROJECT_EDITOR_PLAN.md — one kit, never a second copy): the forma
   panels, the token selection + clipboard and every zone gesture come from
   `useFormaEditorPanels` (forma-studio's public surface). Only the
   Designer-only panels live here: Sections, Navigation, Tables, Modules.

   Josh's law (docs/DESIGNER_UNIFIED_PLAN.md §1): the MENU is the
   permanent, complete access path — docking is convenience. So every
   palette body is rendered by `renderPalette` and hosted EITHER in a
   dock panel OR in a portalled floating panel, never in two places at
   once and never only in one.

   Edits write through useFormaDraft: project copies save in place;
   masters fork the project's copy first (copy-on-write), silently.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { getPanelData } from 'dockview';
import type { Editor } from '@tiptap/react';
import { Blocks, ListTree, Map as MapIcon, Table as TableToolboxGlyph } from 'lucide-react';
import { FormaDock, type FormaDockHandle, type PaletteDef } from '../components/FormaDock';
import { Icon } from '../components/ui/Icon';
import { resolveProjectTokenContent } from '../components/ProjectFormaTokens';
import type { PlaceholderContext } from '../../shared/forma-placeholders';
import {
  FORMA_PANEL_ICONS,
  formaEditorPanels,
  useFormaEditorPanels,
  type DesignWiring,
  type FormaDraft,
  type FormaEditorProject,
  type FormaSelection,
  type PageVariant,
} from '../forma-studio';
import type { DesignerDoc, ManualSection } from './useDesignerDoc';
import { ManualRail } from './ManualRail';
import { NavigationPalette, type NavItem } from './Navigation';
import { ToolPanel } from '../editor-chrome/ToolPanel';
import { PanelDropdown } from '../editor-chrome/PanelDropdown';
import { isOverDockZone, type FloatingTool } from '../editor-chrome/dock';
import { TablesPalette } from './TablesPalette';

/* ─── The palettes of the unified workspace ───
   Left/right docks are SUPERIOR (DESIGN.md §4). Default arrangement:
   Sections + Navigation left (tabbed), the Document in the middle taking
   every remaining pixel, Layout Options + Tables + Tokens right. The rest
   of Forma Studio's panels (Typography, Page Margins, Formats, Watermark,
   Section End, Levels) ship CLOSED here — by the time a spec is in a
   project its forma is already set up, so they are sub-tools you reach for
   from the Toolboxes row, never the thing in your way.

   Panel ids are Forma Studio's. A saved layout from before the unified
   editor still restores: the retired ids ('zone' — the Header & Footer
   panel; 'ds-layout', 'level-props' — renamed to Studio's 'layout' /
   'levels') are dropped by FormaDock's prune, and the new ids join through
   its additive `seen` migration (no storage key bump). */
const FORMA_PLACEMENT: Parameters<typeof formaEditorPanels>[0] = {
  // Lands beside Tables when an older layout already has it; on a fresh
  // layout Tables is not there yet, so FormaDock anchors it right of the
  // Document instead.
  layout: { defaultPosition: { direction: 'within', reference: 'tables' } },
  typography: {
    defaultClosed: true,
    defaultPosition: { direction: 'within', reference: 'layout' },
  },
  margins: { defaultClosed: true, defaultPosition: { direction: 'within', reference: 'layout' } },
  formats: { defaultClosed: true, defaultPosition: { direction: 'within', reference: 'layout' } },
  watermark: { defaultClosed: true, defaultPosition: { direction: 'within', reference: 'layout' } },
  eos: { defaultClosed: true, defaultPosition: { direction: 'within', reference: 'layout' } },
  tokens: { defaultPosition: { direction: 'below', reference: 'layout' } },
  levels: { defaultClosed: true, defaultPosition: { direction: 'within', reference: 'layout' } },
};

const FORMA_PANELS = formaEditorPanels(FORMA_PLACEMENT);
const formaPanel = (id: string): PaletteDef => {
  const p = FORMA_PANELS.find((d) => d.id === id);
  if (!p) throw new Error(`unknown forma panel ${id}`);
  return p;
};

export const DESIGNER_PALETTES: PaletteDef[] = [
  { id: 'canvas', title: 'Document', closable: false, noTab: true },
  {
    id: 'sections',
    title: 'Sections',
    width: 260,
    defaultPosition: { direction: 'left', reference: 'canvas' },
  },
  {
    id: 'navigation',
    title: 'Navigation',
    width: 260,
    defaultPosition: { direction: 'within', reference: 'sections' },
  },
  formaPanel('layout'),
  // T1.2 — every table command in one dockable toolbox (the floating table
  // context toolbar is retired). Tabbed alongside Layout Options.
  {
    id: 'tables',
    title: 'Tables',
    width: 300,
    defaultPosition: { direction: 'within', reference: 'layout' },
  },
  formaPanel('typography'),
  formaPanel('margins'),
  formaPanel('formats'),
  formaPanel('watermark'),
  formaPanel('eos'),
  // Tokens carries BOTH field tokens and project tokens as labeled sections
  // in one body (Josh, 2026-08-26).
  formaPanel('tokens'),
  formaPanel('levels'),
  // Phase 5 — the firm's module library, and the tray of chunks an insert
  // could not place on its own. The SAME panel body the Spec Masters Studio
  // docks (components/ModulesPanel), so inserting a module is one gesture
  // wherever you are. Closed by default: the paper is the hero (§3.4).
  {
    id: 'modules',
    title: 'Modules',
    width: 320,
    // The module TABLE is the thing you insert from — docked narrow or
    // short, it still needs room for its sticky header + a few rows under
    // "In this document" and the tray above it (ModulesPanel's own bounded
    // layout does the rest). Above the 220×140 app default, below the
    // trap floor (DESIGN §3.5 — an empty-side drop must never squeeze the
    // document under ~55%).
    minWidth: 260,
    minHeight: 220,
    defaultClosed: true,
    defaultPosition: { direction: 'right', reference: 'canvas' },
  },
];

/** The palettes THIS user is offered. Without `modules.view` the library is
 *  not part of their Designer at all — hidden, not shown disabled. */
export function designerPalettes(canViewModules: boolean): PaletteDef[] {
  return canViewModules ? DESIGNER_PALETTES : DESIGNER_PALETTES.filter((p) => p.id !== 'modules');
}

/** The Toolboxes row, in reading order: the Designer's own panels first,
 *  then Forma Studio's panels in Forma Studio's own order — the same row a
 *  user learns in either editor. (Dock order above stays as it is: it is
 *  what places a fresh layout.) */
export function designerPanelButtons(canViewModules: boolean): PaletteDef[] {
  const own = ['sections', 'navigation', 'tables', 'modules'];
  const list = designerPalettes(canViewModules).filter((p) => p.id !== 'canvas');
  const rank = (id: string) => {
    const i = own.indexOf(id);
    return i >= 0 ? i : own.length + FORMA_PANELS.findIndex((d) => d.id === id);
  };
  return [...list].sort((x, y) => rank(x.id) - rank(y.id));
}

/** The glyph on each panel's top-bar button. A panel the user can dock,
    float or drop down needs to be recognisable at a glance in all three
    places — so the icon is defined once, next to the panel list. The forma
    panels carry Forma Studio's own glyphs. */
export const PANEL_ICONS: Record<string, React.ReactNode> = {
  sections: <Icon icon={ListTree} em={1.1538} aria-hidden="true" />,
  navigation: <Icon icon={MapIcon} em={1.1538} aria-hidden="true" />,
  tables: <Icon icon={TableToolboxGlyph} em={1.1538} aria-hidden="true" />,
  modules: <Icon icon={Blocks} em={1.1538} aria-hidden="true" />,
  ...FORMA_PANEL_ICONS,
};

export interface WorkspaceProps {
  isDark: boolean;
  userId: string;
  locked: boolean;
  doc: DesignerDoc;
  draft: FormaDraft;
  canEdit: boolean;

  // ── Sections palette (the Manual rail) ──
  sections: ManualSection[];
  activeSectionId: string;
  onOpenSection: (specId: string) => void;
  onManageSections?: () => void;

  // ── Navigation palette ──
  editor: Editor | null;
  onJump: (item: NavItem) => void;

  /** The Modules panel's body (Phase 5) — built by the Designer, which owns
   *  the insert flow, and portalled wherever the panel lives (dock, float or
   *  toolbar dropdown) exactly like every other palette. Null when this user
   *  has no `modules.view`: the palette is then not offered at all. */
  modules?: React.ReactNode;

  // ── The forma (Forma Studio's parts) ──
  /** The ONE selection (levels, tokens, End of Section, band) — owned by
   *  Designer.tsx, shared with the Format Bar and the paper. */
  sel: FormaSelection;
  pageVariant: PageVariant;
  onPageVariant: (v: PageVariant) => void;
  onDragState: (on: boolean) => void;
  dragActive: boolean;
  toast: (msg: string, isError?: boolean) => void;
  /** Open a tool panel by id from the paper (the end-of-section line). */
  onOpenPanel: (id: string) => void;

  // ── Panel hosting (§2.8): dropped down, floating, or docked ──
  /** Panels the user opened without docking them. */
  floatingTools: FloatingTool[];
  onCloseFloating: (id: string) => void;
  onDockFloating: (id: string) => void;
  /** The panel currently dropped down from its top-bar button, if any. */
  dropdown: { id: string; anchor: { left: number; bottom: number } } | null;
  onCloseDropdown: () => void;
  /** Turn the dropdown into a floating panel where it stands. */
  onFloatDropdown: (id: string, at: { left: number; top: number }) => void;
  /** A DOCKED panel was dragged out of every dock zone and released. */
  onUndock: (id: string, at: { left: number; top: number }) => void;
  /** Which palettes the dock currently holds — drives the button states. */
  onDockPanelsChange: (ids: string[]) => void;
  onDockReady: (handle: FormaDockHandle) => void;
  /** A panel BUTTON is being dragged (the workspace shows the same
   *  float-preview feedback a docked panel gets). */
  panelDragId: string | null;
  /** firm_prefs key for this workspace's firm-default layout (§2.9). */
  firmLayoutKey?: string;

  /** Renders the document canvas, given the design wiring. */
  canvas: (wiring: DesignWiring) => React.ReactNode;
}

export function DesignerWorkspace({
  isDark,
  userId,
  locked,
  doc,
  draft,
  canEdit,
  sections,
  activeSectionId,
  onOpenSection,
  onManageSections,
  editor,
  onJump,
  modules,
  sel,
  pageVariant,
  onPageVariant,
  onDragState,
  dragActive,
  toast,
  onOpenPanel,
  floatingTools,
  onCloseFloating,
  onDockFloating,
  dropdown,
  onCloseDropdown,
  onFloatDropdown,
  onUndock,
  onDockPanelsChange,
  onDockReady,
  panelDragId,
  firmLayoutKey,
  canvas,
}: WorkspaceProps) {
  const [dockEls, setDockEls] = React.useState<Record<string, HTMLElement>>({});
  const dockHandleRef = React.useRef<FormaDockHandle | null>(null);

  const forma = draft.forma;

  // The dock's open set, reported upward so the Tools menu can say where a
  // palette lives and decide whether to focus it or float it.
  React.useEffect(() => {
    onDockPanelsChange(Object.keys(dockEls));
  }, [dockEls, onDockPanelsChange]);

  // ─── Project context for placeholders + project tokens ───
  const placeholderCtx = React.useMemo<PlaceholderContext>(
    () => ({
      project: doc.projectMeta,
      issuances: doc.projectIssuances,
      dateFormat: forma?.docSettings?.dateFormat,
    }),
    [doc.projectMeta, doc.projectIssuances, forma?.docSettings?.dateFormat]
  );

  const projectTokens = React.useMemo(() => {
    const proj = doc.project as {
      formaTokens?: Array<{ id: string; name: string; content?: string }>;
    } | null;
    if (!proj?.formaTokens) return [] as Array<{ id: string; name: string; content: string }>;
    return proj.formaTokens.map((t) => ({
      id: t.id,
      name: t.name,
      content: resolveProjectTokenContent(t.content || '', doc.projectMeta),
    }));
  }, [doc.project, doc.projectMeta]);

  const project = React.useMemo<FormaEditorProject>(
    () => ({
      placeholderCtx,
      projectTokens,
      issuanceNames: doc.projectIssuances.map((i) => i.name || '').filter(Boolean),
    }),
    [placeholderCtx, projectTokens, doc.projectIssuances]
  );

  // ─── Forma Studio's panels, selection, clipboard and paper wiring ───
  const panels = useFormaEditorPanels({
    forma,
    edit: draft.edit,
    canEdit,
    toast,
    sel,
    pageVariant,
    onPageVariant,
    onDragState,
    dragActive,
    onOpenPanel,
    project,
    editor,
    logTag: 'Designer',
  });
  const renderFormaPanel = panels.renderPanel;

  /* ─── ONE renderer per palette, hosted by the dock OR by a floating
         panel. This is what makes "the whole feature set works menu-only"
         true rather than aspirational. ─── */
  const renderPalette = React.useCallback(
    (id: string): React.ReactNode => {
      switch (id) {
        case 'sections':
          return (
            <ManualRail
              sections={sections}
              activeId={activeSectionId}
              onOpen={onOpenSection}
              onManageSections={onManageSections}
            />
          );
        case 'navigation':
          return <NavigationPalette editor={editor} forma={forma} onJump={onJump} />;
        case 'modules':
          return modules ?? null;
        case 'tables':
          return <TablesPalette editor={editor} canEdit={canEdit} />;
        default:
          return renderFormaPanel(id);
      }
    },
    [
      sections,
      activeSectionId,
      onOpenSection,
      onManageSections,
      editor,
      forma,
      onJump,
      modules,
      canEdit,
      renderFormaPanel,
    ]
  );

  const titleOf = (id: string) => DESIGNER_PALETTES.find((p) => p.id === id)?.title || id;
  const palettes = React.useMemo(() => designerPalettes(!!modules), [modules]);

  /* ─── DRAG-AWAY UNDOCK (§2.8) ───
     Drag a DOCKED panel off its edge and let go outside every dock zone and
     it becomes a floating box. The commitment happens on RELEASE, never on
     hover, and while the pointer is out there the dock's blue drop preview
     is replaced by a floating-card preview — so "letting go here will float
     it" is visible before you commit, not discovered afterwards.

     Panel BUTTON drags (§2.8's other half) get the identical preview, so the
     two gestures teach each other. */
  const [floatPreview, setFloatPreview] = React.useState<{
    x: number;
    y: number;
    title: string;
  } | null>(null);
  const pendingFloatRef = React.useRef<{ id: string; at: { left: number; top: number } } | null>(
    null
  );
  const onUndockRef = React.useRef(onUndock);
  onUndockRef.current = onUndock;
  const panelDragIdRef = React.useRef(panelDragId);
  panelDragIdRef.current = panelDragId;
  // The listeners below bind once; they read the current title lookup through
  // a ref rather than re-binding on every render.
  const titleOfRef = React.useRef(titleOf);
  titleOfRef.current = titleOf;

  React.useEffect(() => {
    /** The id being dragged, and whether the dock owns it. */
    const dragging = (): { id: string; docked: boolean } | null => {
      const panelId = getPanelData()?.panelId;
      // The Document is the anchored core — it never floats (FormaDock
      // already refuses to close it).
      if (panelId && panelId !== 'canvas') return { id: panelId, docked: true };
      const btn = panelDragIdRef.current;
      return btn ? { id: btn, docked: false } : null;
    };

    const onDragOver = (e: DragEvent) => {
      const drag = dragging();
      if (!drag) return;
      if (isOverDockZone(e.clientX, e.clientY)) {
        pendingFloatRef.current = null;
        setFloatPreview(null);
        return;
      }
      // Out here, letting go floats it — say so, and accept the drop so the
      // cursor stops reading "not allowed".
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      // Only a DOCKED panel is completed here; a button drag is finished by
      // the button's own dragend (it knows whether the dock took it).
      pendingFloatRef.current = drag.docked
        ? { id: drag.id, at: { left: e.clientX, top: e.clientY } }
        : null;
      setFloatPreview({ x: e.clientX, y: e.clientY, title: titleOfRef.current(drag.id) });
    };

    const settle = () => {
      const pending = pendingFloatRef.current;
      pendingFloatRef.current = null;
      setFloatPreview(null);
      if (pending) onUndockRef.current(pending.id, pending.at);
    };

    document.addEventListener('dragover', onDragOver, true);
    document.addEventListener('drop', settle, true);
    document.addEventListener('dragend', settle, true);
    return () => {
      document.removeEventListener('dragover', onDragOver, true);
      document.removeEventListener('drop', settle, true);
      document.removeEventListener('dragend', settle, true);
    };
  }, []);

  return (
    <>
      {panels.wrap(
        <div className="dsg-workspace">
          <FormaDock
            isDark={isDark}
            locked={locked}
            palettes={palettes}
            // Additive designer-scoped key. NEVER reuse sf.formaDock.v4 —
            // the classic editor's key, now historical (nothing writes it
            // any more; a firm may still carry old blobs under it, which
            // is harmless). v2: the workspace itself changed shape (the
            // Sections rail moved INTO the dock), so v1 no longer describes it.
            layoutKey={`sf.designerDock.v2.${userId || 'anon'}`}
            firmLayoutKey={firmLayoutKey}
            panelIcons={PANEL_ICONS}
            onContainersChange={setDockEls}
            onReady={(h) => {
              dockHandleRef.current = h;
              onDockReady(h);
            }}
          />

          {dockEls.canvas && createPortal(canvas(panels.wiring), dockEls.canvas)}

          {palettes
            .filter((p) => p.id !== 'canvas')
            .map((p) =>
              dockEls[p.id] ? (
                <React.Fragment key={p.id}>
                  {createPortal(renderPalette(p.id), dockEls[p.id])}
                </React.Fragment>
              ) : null
            )}

          {/* Floating panels. Same bodies, no dock required. */}
          {floatingTools
            .filter((f) => !dockEls[f.id])
            .map((f, i) => (
              <ToolPanel
                key={f.id}
                title={titleOf(f.id)}
                index={i}
                at={f.at}
                onDock={() => onDockFloating(f.id)}
                onClose={() => onCloseFloating(f.id)}
              >
                {renderPalette(f.id)}
              </ToolPanel>
            ))}

          {/* The panel dropped down from its top-bar button (§2.8). */}
          {dropdown && !dockEls[dropdown.id] && (
            <PanelDropdown
              title={titleOf(dropdown.id)}
              anchor={dropdown.anchor}
              onFloat={() =>
                onFloatDropdown(dropdown.id, {
                  left: dropdown.anchor.left + 180,
                  top: dropdown.anchor.bottom + 14,
                })
              }
              onClose={onCloseDropdown}
            >
              {renderPalette(dropdown.id)}
            </PanelDropdown>
          )}

          {/* "Let go here and it floats" — the preview that replaces the
              dock's drop overlay once the pointer leaves every dock zone. */}
          {floatPreview &&
            createPortal(
              <div
                className="dsg-float-preview"
                aria-hidden="true"
                style={{ left: floatPreview.x, top: floatPreview.y }}
              >
                <span className="dsg-float-preview-bar">{floatPreview.title}</span>
                <span className="dsg-float-preview-hint">Release to float</span>
              </div>,
              document.body
            )}
        </div>
      )}
    </>
  );
}
